export class TTPersistentSession {
	tt_cache;

	constructor( tt_cache ) {
		this.tt_cache = tt_cache;
	}

	isAvailable() {
		const is_available = this.tt_cache?.isAvailable?.() === true;
		Debug.Text( `Persistent session availability check. available=${is_available}`, 'TTPersistentSession.js', 'TTPersistentSession', 'isAvailable', 10 );
		return is_available;
	}

	setViewStackState( state ) {
		const view_count = Array.isArray( state?.views ) ? state.views.length : 0;
		Debug.Text( `Saving view stack state to persistent session. views=${view_count} version=${state?.version ?? 'none'}`, 'TTPersistentSession.js', 'TTPersistentSession', 'setViewStackState', 10 );
		Debug.Arr( state, 'Persistent session TTViewStack state being saved', 'TTPersistentSession.js', 'TTPersistentSession', 'setViewStackState', 10 );
		return this.tt_cache.set( 'view_stack_state', state, 'workspace' );
	}

	getViewStackState() {
		const state = this.tt_cache.get( 'view_stack_state', 'workspace' );
		const view_count = Array.isArray( state?.views ) ? state.views.length : 0;
		Debug.Text( `Loaded view stack state from persistent session. present=${!!state} views=${view_count} version=${state?.version ?? 'none'}`, 'TTPersistentSession.js', 'TTPersistentSession', 'getViewStackState', 10 );
		if ( state ) {
			Debug.Arr( state, 'Persistent session TTViewStack state loaded', 'TTPersistentSession.js', 'TTPersistentSession', 'getViewStackState', 10 );
		}
		return state;
	}

	clearViewStackState() {
		Debug.Text( 'Clearing persisted TTViewStack session state.', 'TTPersistentSession.js', 'TTPersistentSession', 'clearViewStackState', 10 );
		this.tt_cache.remove( 'view_stack_state', 'workspace' );
	}

	/**
	 * Return the dashboard collection last confirmed inside this authenticated browser session.
	 * DashboardView still validates the ID against its permission-filtered schema before use, because role or
	 * collection changes can make a previously valid session selection unavailable while the session remains open.
	 */
	getSelectedDashboardId() {
		const dashboard_id = this.tt_cache.get( 'selected_dashboard_id', 'dashboard_state' );
		return typeof dashboard_id === 'string' && dashboard_id.trim() !== '' ? dashboard_id : null;
	}

	/**
	 * Keep dashboard navigation separate from the durable default-dashboard preference. The cache scope includes
	 * session, user, and company identity, so refresh/navigation can restore the page while a new login starts clean.
	 */
	setSelectedDashboardId( dashboard_id ) {
		if ( typeof dashboard_id !== 'string' || dashboard_id.trim() === '' ) {
			return false;
		}

		return this.tt_cache.set( 'selected_dashboard_id', dashboard_id, 'dashboard_state' );
	}

	/**
	 * Load Wizard resume state for the active authenticated browser session.
	 * Wizard progress must not fall back to database persistence because logout is its ownership boundary.
	 * Reject blank keys and return only non-array object-shaped payloads because TTS supplies the key and resume consumers require that state contract.
	 */
	getWizardState( wizard_key ) {
		if ( typeof wizard_key !== 'string' || wizard_key.trim() === '' ) {
			return null;
		}

		const state = this.tt_cache.get( wizard_key, 'wizard_state' );
		return state && typeof state === 'object' && !Array.isArray( state ) ? state : null;
	}

	/**
	 * Persist Wizard resume state inside the current tab/session scope only.
	 * Blank keys, null, primitives, and arrays fail closed rather than creating ambiguous cache entries that cannot be safely restored.
	 */
	setWizardState( wizard_key, state ) {
		if ( typeof wizard_key !== 'string' || wizard_key.trim() === '' || !state || typeof state !== 'object' || Array.isArray( state ) ) {
			return false;
		}

		return this.tt_cache.set( wizard_key, state, 'wizard_state' );
	}

	/**
	 * Clear one Wizard's resume state without disturbing other session-owned UI state.
	 * Require both available storage and a derived scoped key so a logout race cannot remove state outside an authenticated scope.
	 */
	clearWizardState( wizard_key ) {
		if ( typeof wizard_key !== 'string' || wizard_key.trim() === '' || this.tt_cache?.isAvailable?.() !== true || !this.tt_cache?.getStorageKey?.( wizard_key, 'wizard_state' ) ) {
			return false;
		}

		this.tt_cache.remove( wizard_key, 'wizard_state' );
		return true;
	}

	clearAll() {
		this.tt_cache.cleanAll();
	}
}
